import styled, { keyframes } from 'styled-components';

import RatingStarSvg from '../../../assets/icons/icon-rating-star.svg?react';
import {
  BaseButton,
  BaseTextArea,
  rubikTypographyStyles,
} from '../../../shared/ui';

const starPopReveal = keyframes`
  0% {
    opacity: 0;
    transform: translateY(10px) scale(0.46) rotate(-16deg);
  }

  52% {
    opacity: 1;
    transform: translateY(-2px) scale(1.08) rotate(4deg);
  }

  100% {
    opacity: 1;
    transform: translateY(0) scale(1) rotate(0deg);
  }
`;

const dotPulseReveal = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.2);
  }

  45% {
    opacity: 1;
    transform: scale(1.16);
  }

  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

const trailDrawReveal = keyframes`
  0% {
    opacity: 0;
    stroke-dashoffset: 1;
    transform: translate(4px, 4px) scale(0.88);
  }

  20% {
    opacity: 1;
  }

  100% {
    opacity: 1;
    stroke-dashoffset: 0;
    transform: translate(0, 0) scale(1);
  }
`;

export const RatingCard = styled.article.attrs({
  'data-ui': 'tips-checkout-rating-card',
})`
  background: ${({ theme }) => theme.colors.surface.subtle};
  border-radius: ${({ theme }) => theme.scale.px(11)};
  padding: ${({ theme }) => theme.scale.px(18)}
    ${({ theme }) => theme.scale.px(21)} ${({ theme }) => theme.scale.px(18)};
  display: grid;
  gap: ${({ theme }) => theme.scale.px(14)};
`;

export const Title = styled.p`
  ${rubikTypographyStyles};
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(14)};
  font-weight: 600;
  line-height: 1.18;
`;

export const StarsRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.scale.px(12)};
`;

export const StarButton = styled(BaseButton)<{
  $isFilled: boolean;
}>`
  width: ${({ theme }) => theme.scale.px(40)};
  height: ${({ theme }) => theme.scale.px(40)};
  display: grid;
  place-items: center;
  position: relative;
  overflow: visible;
  color: ${({ $isFilled, theme }) =>
    $isFilled ? theme.colors.brand.primary : theme.colors.text.muted};
`;

export const FeedbackStar = styled(RatingStarSvg).attrs({
  'aria-hidden': true,
  focusable: 'false',
})`
  width: 100%;
  height: 100%;
  display: block;
  color: inherit;

  & path {
    fill: currentColor;
  }
`;

export const FilledFeedbackStar = styled(FeedbackStar)`
  color: ${({ theme }) => theme.colors.brand.primary};
`;

export const ActiveRatingStarSvg = styled.svg.attrs({
  'aria-hidden': true,
  focusable: 'false',
  viewBox: '0 0 53 56',
})`
  width: ${({ theme }) => theme.scale.px(53)};
  height: ${({ theme }) => theme.scale.px(56)};
  position: absolute;
  left: 50%;
  bottom: ${({ theme }) => theme.scale.px(-8)};
  transform: translateX(-50%);
  overflow: visible;
  pointer-events: none;

  .burst-star {
    transform-box: fill-box;
    transform-origin: center;
    animation: ${starPopReveal} 480ms cubic-bezier(0.2, 0.9, 0.28, 1) forwards;
  }

  .burst-dot {
    opacity: 0;
    transform-box: fill-box;
    transform-origin: center;
    animation: ${dotPulseReveal} 320ms ease-out forwards;
  }

  .burst-dot--one {
    animation-delay: 180ms;
  }

  .burst-dot--two {
    animation-delay: 240ms;
  }

  .burst-dot--three {
    animation-delay: 330ms;
  }

  .burst-dot--four {
    animation-delay: 290ms;
  }

  .burst-dot--five {
    animation-delay: 210ms;
  }

  .burst-trail {
    opacity: 0;
    stroke-dasharray: 1;
    stroke-dashoffset: 1;
    transform-box: fill-box;
    transform-origin: center;
    animation: ${trailDrawReveal} 520ms cubic-bezier(0.2, 0.8, 0.22, 1) forwards;
  }

  .burst-trail--one {
    animation-delay: 300ms;
  }

  .burst-trail--two {
    animation-delay: 390ms;
  }

  .burst-trail--three {
    animation-delay: 490ms;
  }

  .burst-trail--four {
    animation-delay: 590ms;
  }
`;

export const FeedbackBlock = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.scale.px(11)};
  margin-top: ${({ theme }) => theme.scale.px(12)};
`;

export const FeedbackMetaRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: end;
  column-gap: ${({ theme }) => theme.scale.px(12)};
`;

export const FeedbackPrompt = styled.p`
  ${rubikTypographyStyles};
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(12)};
  font-weight: 600;
  line-height: 1.18;
`;

export const FeedbackCounter = styled.span`
  font-size: ${({ theme }) => theme.scale.px(12)};
  line-height: 1.25;
  color: ${({ theme }) => theme.colors.border.emphasis};
`;

export const FeedbackTextArea = styled(BaseTextArea)<{ $isPrefilled: boolean }>`
  width: 100%;
  min-height: ${({ theme }) => theme.scale.px(54)};
  box-sizing: border-box;
  border-radius: ${({ theme }) => theme.scale.px(10)};
  background: ${({ theme }) => theme.colors.surface.base};
  padding: ${({ theme }) => theme.scale.px(14)};
  font-size: ${({ theme }) => theme.scale.px(12)};
  line-height: 1.25;
  color: ${({ $isPrefilled, theme }) =>
    $isPrefilled ? theme.colors.border.emphasis : theme.colors.text.primary};

  &::placeholder {
    color: ${({ theme }) => theme.colors.border.emphasis};
  }
`;
